import { Link, useLocation } from 'react-router-dom';
import { brand } from '@/shared/config/brand';
import { useT } from '@/shared/i18n/useT';
import { cn } from '@/shared/utils/cn';
import { BrandMark } from './BrandMark';
import { NavIcon } from './NavIcon';
import { isNavActive, type NavGroup } from './navModel';

export interface SidebarProps {
  groups: NavGroup[];
}

/** Navegación lateral (desde 1024 px): marca, secciones agrupadas y la actual marcada con aria-current. */
export function Sidebar({ groups }: SidebarProps) {
  const { t } = useT();
  const { pathname } = useLocation();
  return (
    <aside className="sticky top-0 hidden h-dvh flex-col border-r border-border-soft bg-card/70 lg:flex">
      <Link
        to="/"
        title={t('aria.brandHome')}
        className="mx-3 mt-4 flex items-center gap-3 rounded-md px-2 py-2 transition-colors duration-fast ease-standard hover:bg-muted"
      >
        <BrandMark size={40} />
        <span className="min-w-0">
          <span className="block font-display text-lg font-bold leading-tight text-fg">
            {brand.name}
          </span>
          <span className="block text-xs leading-snug text-fg-muted">{t('tagline')}</span>
        </span>
      </Link>
      <nav aria-label={t('nav.main')} className="min-h-0 flex-1 overflow-y-auto px-3 pb-6 pt-2">
        {groups.map((group) => (
          <div key={group.id} className="mt-5">
            <p
              id={`nav-group-${group.id}`}
              className="px-3 pb-1.5 text-xs font-semibold uppercase tracking-wider text-fg-muted"
            >
              {t(group.label)}
            </p>
            <ul aria-labelledby={`nav-group-${group.id}`} className="flex flex-col gap-0.5">
              {group.items.map((item) => {
                const active = isNavActive(pathname, item);
                return (
                  <li key={item.id}>
                    <Link
                      to={item.to}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'relative flex h-11 items-center gap-3 rounded-md px-3 text-sm font-medium text-fg-muted',
                        'transition-colors duration-fast ease-standard hover:bg-muted hover:text-fg',
                        active &&
                          'bg-primary-tint font-semibold text-primary-strong hover:bg-primary-tint hover:text-primary-strong',
                      )}
                    >
                      {active ? (
                        <span
                          aria-hidden="true"
                          className="absolute inset-y-2 left-0 w-[3px] rounded-full bg-primary"
                        />
                      ) : null}
                      <NavIcon icon={item.icon} />
                      {t(item.label)}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
